import { useCallback, useEffect, useState } from "react";
import { Pencil, Trash2, AlertCircle, RefreshCw } from "lucide-react";
import {
    addPlatformDefaultSlot,
    deletePlatformDefaultSlot,
    getPlatformDefaultSlots,
    updatePlatformDefaultSlot,
} from "../service/api";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { TimePicker } from "../components/ui/time-picker";
import { Card, CardContent, CardHeader, CardTitle } from "../components/ui/card";
import {
    Table, TableBody, TableCell, TableHead, TableHeader, TableRow,
} from "../components/ui/table";
import {
    Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle,
} from "../components/ui/dialog";
import { Switch } from "../components/ui/switch";
import { Alert, AlertDescription } from "../components/ui/alert";

type DefaultSlotRow = {
    id: number;
    from_time?: string;
    to_time?: string;
    price?: string | number;
    is_available?: number | boolean;
    not_available_reason?: string | null;
    created_at?: string;
    updated_at?: string;
};

function extractSlotList(res: { data?: unknown }): DefaultSlotRow[] {
    const d = res?.data as Record<string, unknown> | undefined;
    if (!d) return [];
    if (Array.isArray(d)) return d as DefaultSlotRow[];
    const inner = d.data;
    if (Array.isArray(inner)) return inner as DefaultSlotRow[];
    if (inner && typeof inner === "object" && Array.isArray((inner as { data?: unknown }).data)) {
        return (inner as { data: DefaultSlotRow[] }).data;
    }
    return [];
}

function apiErrorMessage(err: unknown, fallback: string): string {
    if (typeof err === "object" && err !== null && "response" in err) {
        const data = (err as { response?: { data?: { message?: string } } }).response?.data;
        if (data?.message && typeof data.message === "string") return data.message;
    }
    return fallback;
}

function timeForInput(raw: string | undefined): string {
    if (!raw) return "";
    const s = String(raw).trim();
    if (s.length >= 5) return s.slice(0, 5);
    return s;
}

function timeForApi(value: string): string {
    const v = value.trim();
    if (!v) return v;
    return v.length === 5 ? v : v.slice(0, 5);
}

const emptyAddForm = {
    from_time: "",
    to_time: "",
    price: "",
    is_available: true,
    not_available_reason: "",
};

const DefaultSlots = () => {
    const [rows, setRows] = useState<DefaultSlotRow[]>([]);
    const [loading, setLoading] = useState(false);
    const [saving, setSaving] = useState(false);
    const [banner, setBanner] = useState<{ type: "success" | "error"; text: string } | null>(null);
    const [addForm, setAddForm] = useState(emptyAddForm);
    const [deleteModal, setDeleteModal] = useState(false);
    const [deleteId, setDeleteId] = useState<number | null>(null);
    const [editOpen, setEditOpen] = useState(false);
    const [editRow, setEditRow] = useState<DefaultSlotRow | null>(null);
    const [editForm, setEditForm] = useState({
        from_time: "",
        to_time: "",
        price: "",
        is_available: true,
        not_available_reason: "",
    });

    const loadSlots = useCallback(async () => {
        setLoading(true);
        setBanner(null);
        try {
            const res = await getPlatformDefaultSlots();
            setRows(extractSlotList(res));
        } catch (err) {
            setBanner({ type: "error", text: apiErrorMessage(err, "Could not load default slots.") });
            setRows([]);
        } finally {
            setLoading(false);
        }
    }, []);

    useEffect(() => {
        loadSlots();
    }, [loadSlots]);

    const openEdit = (row: DefaultSlotRow) => {
        setEditRow(row);
        setEditForm({
            from_time: timeForInput(row.from_time),
            to_time: timeForInput(row.to_time),
            price: row.price != null ? String(row.price) : "",
            is_available: row.is_available === 1 || row.is_available === true,
            not_available_reason: row.not_available_reason ?? "",
        });
        setEditOpen(true);
    };

    const handleAddSubmit = async (e: React.FormEvent) => {
        e.preventDefault();
        setBanner(null);
        if (!addForm.from_time || !addForm.to_time || !addForm.price.trim()) {
            setBanner({ type: "error", text: "From time, to time, and price are required." });
            return;
        }
        setSaving(true);
        try {
            await addPlatformDefaultSlot({
                from_time: timeForApi(addForm.from_time),
                to_time: timeForApi(addForm.to_time),
                price: addForm.price.trim(),
                is_available: addForm.is_available ? 1 : 0,
                not_available_reason: addForm.not_available_reason.trim() || null,
            });
            setBanner({ type: "success", text: "Default slot added for all turfs." });
            setAddForm(emptyAddForm);
            await loadSlots();
        } catch (err) {
            setBanner({
                type: "error",
                text: apiErrorMessage(err, "Could not add slot. Duplicate time range may already exist (409)."),
            });
        } finally {
            setSaving(false);
        }
    };

    const handleEditSubmit = async (e: React.FormEvent) => {
        e.preventDefault();
        if (!editRow) return;
        setBanner(null);
        if (!editForm.from_time || !editForm.to_time || !editForm.price.trim()) {
            setBanner({ type: "error", text: "From time, to time, and price are required." });
            return;
        }
        setSaving(true);
        try {
            await updatePlatformDefaultSlot({
                id: editRow.id,
                from_time: timeForApi(editForm.from_time),
                to_time: timeForApi(editForm.to_time),
                price: editForm.price.trim(),
                is_available: editForm.is_available ? 1 : 0,
                not_available_reason: editForm.not_available_reason.trim() || null,
            });
            setBanner({ type: "success", text: "Default slot updated." });
            setEditOpen(false);
            setEditRow(null);
            await loadSlots();
        } catch (err) {
            setBanner({ type: "error", text: apiErrorMessage(err, "Could not update slot.") });
        } finally {
            setSaving(false);
        }
    };

    const confirmDelete = (id: number) => {
        setDeleteId(id);
        setDeleteModal(true);
    };

    const runDelete = async () => {
        if (deleteId == null) return;
        setSaving(true);
        try {
            await deletePlatformDefaultSlot({ id: deleteId });
            setBanner({ type: "success", text: "Default slot removed." });
            setDeleteModal(false);
            setDeleteId(null);
            await loadSlots();
        } catch (err) {
            setBanner({ type: "error", text: apiErrorMessage(err, "Could not delete slot.") });
        } finally {
            setSaving(false);
        }
    };

    const availLabel = (row: DefaultSlotRow) =>
        row.is_available === 1 || row.is_available === true ? "Yes" : "No";

    return (
        <div className="w-full max-w-6xl mx-auto pb-8 space-y-6">
            {/* Header */}
            <div className="flex flex-col gap-3 sm:flex-row sm:items-start sm:justify-between">
                <div>
                    <h1 className="text-2xl font-semibold text-foreground">Default slot management</h1>
                    <p className="mt-1 text-sm text-muted-foreground">
                        These defaults define standard time windows for the platform. Each turf can still add, edit, or
                        remove its own slots in the partner app without changing the global defaults listed here.
                    </p>
                </div>
                <Button variant="outline" size="sm" onClick={() => loadSlots()}>
                    <RefreshCw className="h-4 w-4" />
                </Button>
            </div>

            {/* Banner alert */}
            {banner && (
                <Alert variant={banner.type === "error" ? "destructive" : "default"} className="flex flex-row items-center justify-between">
                    <AlertDescription className="flex-1">{banner.text}</AlertDescription>
                    <Button variant="ghost" size="sm" className="ml-4 shrink-0" onClick={() => setBanner(null)}>
                        Dismiss
                    </Button>
                </Alert>
            )}

            {/* Add form */}
            <Card>
                <CardHeader className="pb-2">
                    <CardTitle className="text-base">Add default slot</CardTitle>
                    <p className="text-sm text-muted-foreground">Creates a default time window for all turfs on the platform.</p>
                </CardHeader>
                <CardContent>
                    <form onSubmit={handleAddSubmit} className="space-y-5">
                        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
                            <div className="space-y-1.5">
                                <Label htmlFor="add-from">From</Label>
                                <TimePicker
                                    id="add-from"
                                    value={addForm.from_time}
                                    onChange={(v) => setAddForm((f) => ({ ...f, from_time: v }))}
                                    className="w-full"
                                />
                            </div>
                            <div className="space-y-1.5">
                                <Label htmlFor="add-to">To</Label>
                                <TimePicker
                                    id="add-to"
                                    value={addForm.to_time}
                                    onChange={(v) => setAddForm((f) => ({ ...f, to_time: v }))}
                                    className="w-full"
                                />
                            </div>
                            <div className="space-y-1.5 sm:col-span-2 lg:col-span-1">
                                <Label htmlFor="add-price">Price</Label>
                                <Input
                                    id="add-price"
                                    type="text"
                                    inputMode="decimal"
                                    placeholder="e.g. 500"
                                    value={addForm.price}
                                    onChange={(e) => setAddForm((f) => ({ ...f, price: e.target.value }))}
                                />
                            </div>
                        </div>
                        <div className="flex items-center gap-3">
                            <Switch
                                id="add-available"
                                checked={addForm.is_available}
                                onCheckedChange={(checked) => setAddForm((f) => ({ ...f, is_available: checked }))}
                            />
                            <Label htmlFor="add-available" className="cursor-pointer">Available</Label>
                        </div>
                        <div className="space-y-1.5">
                            <Label htmlFor="add-reason">
                                Unavailable reason <span className="text-muted-foreground font-normal">(optional)</span>
                            </Label>
                            <Input
                                id="add-reason"
                                type="text"
                                placeholder="Shown when slot is marked unavailable"
                                value={addForm.not_available_reason}
                                onChange={(e) => setAddForm((f) => ({ ...f, not_available_reason: e.target.value }))}
                            />
                        </div>
                        <div className="pt-2 border-t">
                            <Button type="submit" size="sm" disabled={saving}>
                                {saving ? <span className="h-4 w-4 animate-spin rounded-full border-2 border-current border-t-transparent mr-2" /> : null}
                                Add slot
                            </Button>
                        </div>
                    </form>
                </CardContent>
            </Card>

            {/* Table card */}
            <Card className="overflow-hidden">
                <div className="px-6 py-4 border-b">
                    <h2 className="text-base font-semibold">All default slots</h2>
                    <p className="text-sm text-muted-foreground mt-1">
                        Templates apply platform-wide; availability on each date still respects turf weekly slots and overrides when set.
                    </p>
                </div>
                {loading ? (
                    <div className="flex min-h-[200px] items-center justify-center">
                        <div className="h-8 w-8 animate-spin rounded-full border-2 border-primary border-t-transparent" />
                    </div>
                ) : (
                    <div className="overflow-x-auto">
                        <Table>
                            <TableHeader>
                                <TableRow>
                                    <TableHead className="w-12">#</TableHead>
                                    <TableHead>From</TableHead>
                                    <TableHead>To</TableHead>
                                    <TableHead>Price</TableHead>
                                    <TableHead>Available</TableHead>
                                    <TableHead className="min-w-[8rem]">Reason</TableHead>
                                    <TableHead className="text-right w-24">Actions</TableHead>
                                </TableRow>
                            </TableHeader>
                            <TableBody>
                                {rows.length === 0 ? (
                                    <TableRow>
                                        <TableCell colSpan={7} className="text-center text-muted-foreground py-14">
                                            No default slots yet. Add one using the form above.
                                        </TableCell>
                                    </TableRow>
                                ) : (
                                    rows.map((row, i) => (
                                        <TableRow key={row.id}>
                                            <TableCell className="text-muted-foreground">{i + 1}</TableCell>
                                            <TableCell className="font-mono text-sm tabular-nums">{row.from_time ?? "—"}</TableCell>
                                            <TableCell className="font-mono text-sm tabular-nums">{row.to_time ?? "—"}</TableCell>
                                            <TableCell className="font-medium">{row.price ?? "—"}</TableCell>
                                            <TableCell>{availLabel(row)}</TableCell>
                                            <TableCell
                                                className="max-w-[14rem] lg:max-w-xs truncate text-sm"
                                                title={row.not_available_reason ?? ""}
                                            >
                                                {row.not_available_reason || "—"}
                                            </TableCell>
                                            <TableCell className="text-right">
                                                <Button
                                                    variant="ghost"
                                                    size="icon"
                                                    className="h-8 w-8"
                                                    onClick={() => openEdit(row)}
                                                >
                                                    <Pencil className="h-4 w-4" />
                                                </Button>
                                                <Button
                                                    variant="ghost"
                                                    size="icon"
                                                    className="h-8 w-8 text-destructive hover:text-destructive"
                                                    onClick={() => confirmDelete(row.id)}
                                                >
                                                    <Trash2 className="h-4 w-4" />
                                                </Button>
                                            </TableCell>
                                        </TableRow>
                                    ))
                                )}
                            </TableBody>
                        </Table>
                    </div>
                )}
            </Card>

            {/* Edit dialog */}
            <Dialog open={editOpen} onOpenChange={(open) => !saving && setEditOpen(open)}>
                <DialogContent className="max-w-md">
                    <DialogHeader>
                        <DialogTitle>Edit default slot</DialogTitle>
                        <DialogDescription>Update the time window, price, and availability for this default slot.</DialogDescription>
                    </DialogHeader>
                    <form onSubmit={handleEditSubmit} className="space-y-4 py-2">
                        <div className="space-y-1.5">
                            <Label>From</Label>
                            <TimePicker
                                value={editForm.from_time}
                                onChange={(v) => setEditForm((f) => ({ ...f, from_time: v }))}
                                className="w-full"
                            />
                        </div>
                        <div className="space-y-1.5">
                            <Label>To</Label>
                            <TimePicker
                                value={editForm.to_time}
                                onChange={(v) => setEditForm((f) => ({ ...f, to_time: v }))}
                                className="w-full"
                            />
                        </div>
                        <div className="space-y-1.5">
                            <Label>Price</Label>
                            <Input
                                type="text"
                                value={editForm.price}
                                onChange={(e) => setEditForm((f) => ({ ...f, price: e.target.value }))}
                            />
                        </div>
                        <div className="flex items-center gap-3">
                            <Switch
                                id="edit-available"
                                checked={editForm.is_available}
                                onCheckedChange={(checked) => setEditForm((f) => ({ ...f, is_available: checked }))}
                            />
                            <Label htmlFor="edit-available" className="cursor-pointer">Available</Label>
                        </div>
                        <div className="space-y-1.5">
                            <Label>Unavailable reason</Label>
                            <Input
                                type="text"
                                value={editForm.not_available_reason}
                                onChange={(e) => setEditForm((f) => ({ ...f, not_available_reason: e.target.value }))}
                            />
                        </div>
                        <DialogFooter>
                            <Button type="button" variant="outline" disabled={saving} onClick={() => setEditOpen(false)}>Cancel</Button>
                            <Button type="submit" disabled={saving}>
                                {saving ? <span className="h-4 w-4 animate-spin rounded-full border-2 border-current border-t-transparent mr-2" /> : null}
                                Save
                            </Button>
                        </DialogFooter>
                    </form>
                </DialogContent>
            </Dialog>

            {/* Delete confirmation dialog */}
            <Dialog open={deleteModal} onOpenChange={setDeleteModal}>
                <DialogContent className="max-w-sm">
                    <DialogHeader className="items-center text-center">
                        <AlertCircle className="h-12 w-12 text-destructive mx-auto mb-2" />
                        <DialogTitle>Delete this default slot?</DialogTitle>
                        <DialogDescription>
                            This removes the platform default row. Turf-specific slots already created are not affected.
                        </DialogDescription>
                    </DialogHeader>
                    <DialogFooter className="flex-row justify-center gap-2 sm:justify-center">
                        <Button variant="outline" onClick={() => setDeleteModal(false)}>Cancel</Button>
                        <Button variant="destructive" disabled={saving} onClick={runDelete}>
                            {saving ? <span className="h-4 w-4 animate-spin rounded-full border-2 border-current border-t-transparent mr-2" /> : null}
                            Delete
                        </Button>
                    </DialogFooter>
                </DialogContent>
            </Dialog>
        </div>
    );
};

export default DefaultSlots;
